import React, { useState, useRef, useCallback, useEffect } from 'react'
import { useTranslation } from 'react-i18next'
import { Button } from 'components/ui/Button'
import { Modal } from 'components/ui/Modal'
import { Badge } from 'components/ui/Badge'
import { Progress } from 'components/ui/Progress'
import { Alert, AlertDescription } from 'components/ui/Alert'
import { CustomSelect } from 'components/ui/CustomSelect'
import { 
  Upload, 
  FileText, 
  File, 
  X, 
  CheckCircle, 
  AlertCircle, 
  Loader2, 
  Eye,
  EyeOff,
  Download,
  RefreshCw,
  Sparkles,
  Zap
} from 'lucide-react'
import { offersApi, uploadsApi, hiringProcessApi, type HiringProcessTemplate } from 'lib/api'

interface ImportOfferModalProps {
  isOpen: boolean
  onClose: () => void
  onOfferImported: () => void
}

interface ProcessingStep {
  id: string
  name: string
  status: 'pending' | 'processing' | 'completed' | 'error'
  message?: string
  progress?: number
}

interface ImportResult {
  success: boolean
  offer?: any
  extractedText?: string
  error?: string
  warnings?: string[]
}

// Map backend warning messages to i18n translation keys
const WARNING_TRANSLATION_KEYS: Record<string, string> = {
  'No job title found in document': 'offers.import.warnings.noJobTitle',
  'No job description found in document': 'offers.import.warnings.noDescription',
  'No requirements found in document': 'offers.import.warnings.noRequirements',
}

export function ImportOfferModal({ isOpen, onClose, onOfferImported }: ImportOfferModalProps) {
  const { t, i18n } = useTranslation()
  const fileInputRef = useRef<HTMLInputElement>(null)
  const [selectedFile, setSelectedFile] = useState<File | null>(null)
  const [mode, setMode] = useState<'document' | 'url'>('document')
  const [urlInput, setUrlInput] = useState<string>('')
  const [siteSelection, setSiteSelection] = useState<'linkedin' | 'moncallcenter' | 'other' | ''>('')
  const [isProcessing, setIsProcessing] = useState(false)
  const [currentStep, setCurrentStep] = useState<string>('')
  const [overallProgress, setOverallProgress] = useState(0)
  const [aiAvailable, setAiAvailable] = useState<boolean | null>(null)
  const [checkingAI, setCheckingAI] = useState(false)
  const [templates, setTemplates] = useState<HiringProcessTemplate[]>([])
  const [selectedTemplateId, setSelectedTemplateId] = useState<number | null>(null)
  const [processingSteps, setProcessingSteps] = useState<ProcessingStep[]>([
    { id: 'validate', name: 'Validating File', status: 'pending', progress: 0 },
    { id: 'ai-process', name: 'AI Analysis', status: 'pending', progress: 0 },
    { id: 'save', name: 'Saving Offer', status: 'pending', progress: 0 }
  ])
  const [result, setResult] = useState<ImportResult | null>(null)
  const [showExtractedText, setShowExtractedText] = useState(false)
  const [showGeneratedData, setShowGeneratedData] = useState(false)
  const [retryCount, setRetryCount] = useState(0)

  const resetState = useCallback(() => {
    setSelectedFile(null)
    setResult(null)
    setShowExtractedText(false)
    setShowGeneratedData(false)
    setRetryCount(0)
    setOverallProgress(0)
    setCurrentStep('')
    setProcessingSteps(prev => prev.map(step => ({ 
      ...step, 
      status: 'pending', 
      message: undefined, 
      progress: 0 
    })))
  }, [])

  // Check AI availability when modal opens
  useEffect(() => {
    if (isOpen) {
      checkAIAvailability()
    }
  }, [isOpen])

  // Load hiring templates when modal opens
  useEffect(() => {
    const loadTemplates = async () => {
      try {
        const res = await hiringProcessApi.getTemplates()
        const items = Array.isArray(res.data) ? res.data : (res.data as any).results || []
        const active = items.filter((tpl: HiringProcessTemplate) => tpl.is_active)
        setTemplates(active)
        const defaultTemplate = active.find(tpl => tpl.is_default)
        setSelectedTemplateId(defaultTemplate ? defaultTemplate.id : active[0]?.id ?? null)
      } catch (error) {
        console.error('Error loading hiring templates:', error)
        setTemplates([])
        setSelectedTemplateId(null)
      }
    }

    if (isOpen) {
      loadTemplates()
    }
  }, [isOpen])

  const checkAIAvailability = async () => {
    setCheckingAI(true)
    try {
      const response = await uploadsApi.checkAiAvailability()
      setAiAvailable(response.data.ai_available || response.data.status === 'available')
    } catch (error) {
      console.error('Failed to check AI availability:', error)
      setAiAvailable(false)
    } finally {
      setCheckingAI(false)
    }
  }

  const handleFileSelect = (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0]
    if (file) {
      const allowedTypes = [
        'application/pdf',
        'application/vnd.openxmlformats-officedocument.wordprocessingml.document',
        'application/msword'
      ]
      
      if (!allowedTypes.includes(file.type)) {
        setResult({
          success: false,
          error: t('offers.import.invalidFileType')
        })
        return
      }
      
      if (file.size > 10 * 1024 * 1024) { // 10MB limit
        setResult({
          success: false,
          error: t('offers.import.fileTooLarge')
        })
        return
      }
      
      setSelectedFile(file)
      setResult(null)
    }
  }

  const updateStepStatus = useCallback((stepId: string, status: ProcessingStep['status'], message?: string, progress?: number) => {
    setProcessingSteps(prev => prev.map(step => 
      step.id === stepId 
        ? { ...step, status, message, progress }
        : step
    ))
  }, [])

  const simulateProgress = useCallback((stepId: string, duration: number = 1000) => {
    return new Promise<void>((resolve) => {
      let progress = 0
      const interval = setInterval(() => {
        progress += Math.random() * 20
        if (progress >= 100) {
          progress = 100
          clearInterval(interval)
          resolve()
        }
        updateStepStatus(stepId, 'processing', undefined, progress)
      }, duration / 10)
    })
  }, [updateStepStatus])

  const handleImport = async () => {
    // Basic validation depending on mode
    if (mode === 'document') {
      if (!selectedFile) return

      // Prevent import if AI is not available (including when still checking)
      if (checkingAI || aiAvailable === false || aiAvailable === null) {
        setResult({
          success: false,
          error: t('offers.import.aiNotAvailableError')
        })
        return
      }
    } else {
      if (!urlInput || urlInput.trim().length === 0) {
        setResult({ success: false, error: t('offers.import.urlMissing') || 'Please provide a URL' })
        return
      }
    }

    setIsProcessing(true)
    setResult(null)
    setOverallProgress(0)

    try {
      // Simple progress bar - no detailed steps
      setCurrentStep(t('offers.import.processingDocument'))
      setOverallProgress(10)
      
      // Simulate small delay for user feedback
      await new Promise(resolve => setTimeout(resolve, 300))
      
      let response: any

      if (mode === 'document') {
        // Call the API to import the document
        setOverallProgress(30)
        setCurrentStep(t('offers.import.uploadingStep'))

        response = await offersApi.importFromDocument(selectedFile as File, i18n.language, selectedTemplateId)
      } else {
        setOverallProgress(30)
        setCurrentStep(t('offers.import.fetchingUrl'))
        response = await offersApi.importFromURL(urlInput.trim(), siteSelection || undefined, i18n.language, selectedTemplateId)
      }
      
      setOverallProgress(60)
      setCurrentStep(t('offers.import.aiStep'))
      
      // Check if AI returned valid data
      if (!response.data.offer) {
        throw new Error(t('offers.import.aiExtractFailed'))
      }

      // Validate that we got essential fields
              const offerData = response.data.offer
      if (!offerData.job_title && !offerData.title) {
        // AI might have returned an error or incomplete data
        const errorMsg = (response.data as { error?: string }).error || t('offers.import.aiNoData')
        throw new Error(errorMsg)
      }

      setOverallProgress(100)
      setCurrentStep(t('offers.import.completedStep'))

      setResult({
        success: true,
                offer: offerData,
        extractedText: response.data.extracted_text,
        warnings: response.data.warnings || []
      })

      // Auto-close after success
      setTimeout(() => {
        onOfferImported()
        handleClose()
      }, 2000)

    } catch (error: any) {
      console.error('Import failed:', error)
      
      // Detect different types of errors
      let errorMessage = t('offers.import.error')
      
      if (error.response?.data?.error) {
        errorMessage = error.response.data.error
      } else if (error.response?.data?.details) {
        errorMessage = error.response.data.details
      } else if (error.message) {
        errorMessage = error.message
      } else if (error.response?.status === 503) {
        errorMessage = t('offers.import.aiTemporarilyUnavailable')
      } else if (error.response?.status === 500) {
        errorMessage = t('offers.import.aiProcessingError')
      }
      
      setResult({
        success: false,
        error: errorMessage
      })
      
      setCurrentStep(t('offers.import.failedStep'))
      setOverallProgress(0)
    } finally {
      setIsProcessing(false)
    }
  }

  const handleRetry = () => {
    setRetryCount(prev => prev + 1)
    setResult(null)
    handleImport()
  }

  const handleClose = () => {
    if (!isProcessing) {
      resetState()
      onClose()
    }
  }

  const getStepIcon = (status: ProcessingStep['status']) => {
    switch (status) {
      case 'completed':
        return <CheckCircle className="h-4 w-4 text-green-500" />
      case 'error':
        return <AlertCircle className="h-4 w-4 text-red-500" />
      case 'processing':
        return <Loader2 className="h-4 w-4 text-blue-500 animate-spin" />
      default:
        return <div className="h-4 w-4 rounded-full border-2 border-gray-300" />
    }
  }

  const getStepColor = (status: ProcessingStep['status']) => {
    switch (status) {
      case 'completed':
        return 'text-green-600'
      case 'error':
        return 'text-red-600'
      case 'processing':
        return 'text-blue-600'
      default:
        return 'text-gray-500'
    }
  }

  const formatFileSize = (bytes: number) => {
    if (bytes === 0) return '0 Bytes'
    const k = 1024
    const sizes = ['Bytes', 'KB', 'MB', 'GB']
    const i = Math.floor(Math.log(bytes) / Math.log(k))
    return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + ' ' + sizes[i]
  }

  return (
    <Modal 
      isOpen={isOpen} 
      onClose={handleClose} 
      title={t('offers.import.title')}
      className="max-w-2xl"
    >
      <div className="space-y-6">
        {/* Hiring template selection */}
        {!isProcessing && !result && (
          <div className="rounded-2xl border bg-card shadow-sm">
            <div className="px-5 py-4 border-b">
              <div className="text-sm font-semibold text-foreground">
                {t('hiringProcess.template', 'Hiring template')}
              </div>
              <div className="text-xs text-muted-foreground mt-1">
                {t('offers.import.hiringTemplateHelp', 'Choose which hiring workflow this imported offer will follow.')}
              </div>
            </div>
            <div className="p-5">
              <CustomSelect
                options={templates.map((tpl) => ({
                  value: String(tpl.id),
                  label: tpl.is_default ? `${tpl.name} (${t('common.default', 'Default')})` : tpl.name,
                }))}
                value={selectedTemplateId != null ? String(selectedTemplateId) : ''}
                onChange={(v) => setSelectedTemplateId(v ? Number(v) : null)}
                placeholder={t('offers.import.hiringTemplatePlaceholder', 'Select a hiring template')}
                disabled={templates.length === 0}
              />
              {templates.length === 0 && (
                <div className="mt-3">
                  <Alert variant="warning">
                    <AlertDescription>
                      {t('notifications.noHiringProcessConfigured', 'No hiring process configured')}
                    </AlertDescription>
                  </Alert>
                </div>
              )}
            </div>
          </div>
        )}

        {/* Mode Selector: Document vs URL */}
        <div className="flex items-center gap-2">
          <button
            onClick={() => setMode('document')}
            className={`px-3 py-1 rounded-full text-sm ${
              mode === 'document'
                ? 'bg-primary text-primary-foreground'
                : 'bg-muted/20 text-muted-foreground'
            }`}
          >
            {t('offers.import.mode.document')}
          </button>
          <button
            onClick={() => setMode('url')}
            className={`px-3 py-1 rounded-full text-sm ${
              mode === 'url'
                ? 'bg-primary text-primary-foreground'
                : 'bg-muted/20 text-muted-foreground'
            }`}
          >
            {t('offers.import.mode.url')}
          </button>
        </div>
        {/* File Upload Section */}
        {!isProcessing && !result && mode === 'document' && (
          <div className="rounded-2xl border bg-card shadow-sm">
            <div className="px-5 py-4 border-b flex items-center justify-between">
              <div className="flex items-center gap-2">
                <div className="h-9 w-9 rounded-full bg-primary/10 text-primary flex items-center justify-center">
                  <Upload className="h-4 w-4" />
                </div>
                <div>
                  <p className="text-sm font-semibold">{t('offers.import.selectDocument')}</p>
                  <p className="text-xs text-muted-foreground">
                    {t('offers.import.helpText')}
                  </p>
                </div>
              </div>
              {selectedFile && (
                <span className="text-[10px] font-medium text-muted-foreground/70 px-2 py-1 rounded-md bg-muted/50 whitespace-nowrap">
                  {formatFileSize(selectedFile.size)}
                </span>
              )}
            </div>
            <div className="p-5 space-y-4">
              <div className="flex items-center gap-2 text-xs font-medium text-muted-foreground">
                {checkingAI ? (
                  <>
                    <Loader2 className="h-3.5 w-3.5 animate-spin" />
                    <span>{t('offers.import.aiChecking')}</span>
                  </>
                ) : aiAvailable === false ? (
                  <>
                    <AlertCircle className="h-3.5 w-3.5 text-red-500" />
                    <span>{t('offers.import.aiUnavailableShort')}</span>
                  </>
                ) : (
                  <>
                    <Sparkles className="h-3.5 w-3.5 text-green-500" />
                    <span>{t('offers.import.aiReady')}</span>
                  </>
                )}
              </div>

              <div
                className="rounded-2xl border-2 border-dashed border-muted-foreground/40 p-6 text-center transition hover:border-primary/70 cursor-pointer bg-muted/30"
                onClick={() => fileInputRef.current?.click()}
              >
                <input
                  ref={fileInputRef}
                  type="file"
                  accept=".pdf,.doc,.docx"
                  onChange={handleFileSelect}
                  className="hidden"
                />

                {selectedFile ? (
                  <div className="space-y-3">
                    <div className="flex items-center justify-center gap-2 text-sm font-medium">
                      <FileText className="h-5 w-5 text-primary" />
                      <span className="truncate max-w-[220px]">{selectedFile.name}</span>
                      <button
                        onClick={(e) => {
                          e.stopPropagation()
                          setSelectedFile(null)
                        }}
                        className="text-muted-foreground hover:text-destructive"
                      >
                        <X className="h-4 w-4" />
                      </button>
                    </div>
                    <div className="flex items-center justify-center gap-2 text-xs text-muted-foreground">
                      <span>{selectedFile.type.split('/')[1]?.toUpperCase()}</span>
                      <span>•</span>
                      <span>{formatFileSize(selectedFile.size)}</span>
                    </div>
                    <p className="text-xs text-muted-foreground">
                      {t('offers.import.replaceFileHint')}
                    </p>
                  </div>
                ) : (
                  <div className="space-y-3">
                    <div className="h-12 w-12 rounded-full bg-primary/10 text-primary flex items-center justify-center mx-auto">
                      <Upload className="h-5 w-5" />
                    </div>
                    <p className="text-sm font-semibold">{t('offers.import.clickToUpload')}</p>
                    <p className="text-xs text-muted-foreground">
                      {t('offers.import.fileTypes')}
                    </p>
                  </div>
                )}
              </div>
            </div>
          </div>
        )}

        {/* URL Import Section */}
        {!isProcessing && !result && mode === 'url' && (
          <div className="rounded-2xl border bg-card dark:bg-gray-900/60 border-border dark:border-gray-800 shadow-sm p-5">
            <div className="mb-3">
              <p className="text-sm font-semibold text-foreground">
                {t('offers.import.url.title')}
              </p>
              <div className="flex items-center gap-2 text-xs font-medium text-muted-foreground">
                {checkingAI ? (
                  <>
                    <Loader2 className="h-3.5 w-3.5 animate-spin" />
                    <span>{t('offers.import.aiChecking')}</span>
                  </>
                ) : aiAvailable === false ? (
                  <>
                    <AlertCircle className="h-3.5 w-3.5 text-red-500" />
                    <span>{t('offers.import.aiUnavailableShort')}</span>
                  </>
                ) : (
                  <>
                    <Sparkles className="h-3.5 w-3.5 text-green-500" />
                    <span>{t('offers.import.aiReady')}</span>
                  </>
                )}
              </div>
            </div>

            <div className="space-y-3">
              <input
                type="text"
                value={urlInput}
                onChange={(e) => setUrlInput(e.target.value)}
                placeholder={t('offers.import.url.placeholder')}
                className="w-full rounded-md border border-border dark:border-gray-700 bg-background dark:bg-gray-900 text-sm text-foreground placeholder:text-muted-foreground p-2"
              />
              {/* Optional: site selector with translations and dark mode */}
              {/*
              <div className="flex gap-2 items-center">
                <label className="text-xs text-muted-foreground">
                  {t('offers.import.url.siteLabel')}
                </label>
                <select
                  value={siteSelection}
                  onChange={(e) => setSiteSelection(e.target.value as any)}
                  className="rounded-md border border-border dark:border-gray-700 bg-background dark:bg-gray-900 text-sm text-foreground p-1"
                >
                  <option value="">{t('offers.import.url.autoDetect')}</option>
                  <option value="linkedin">{t('offers.import.url.linkedin')}</option>
                  <option value="moncallcenter">{t('offers.import.url.moncallcenter')}</option>
                  <option value="other">{t('offers.import.url.other')}</option>
                </select>
              </div>
              */}
            </div>
          </div>
        )}

        {/* Error Display */}
        {result && !result.success && (
          <Alert variant="destructive">
            <AlertCircle className="h-4 w-4" />
            <AlertDescription>
              <div className="space-y-2">
                <p>{result.error}</p>
                {retryCount > 0 && (
                  <p className="text-sm">
                    {t('offers.import.retryAttempt', { count: retryCount })}
                  </p>
                )}
              </div>
            </AlertDescription>
          </Alert>
        )}

        {/* Processing Section - Simple Progress Bar */}
        {isProcessing && (
          <div className="rounded-2xl border bg-muted/40 p-5 space-y-4">
            <div className="flex items-center gap-2 text-sm font-medium">
              <Loader2 className="h-5 w-5 animate-spin text-primary" />
              <span>{currentStep || t('offers.import.processingFallback')}</span>
            </div>
            <div className="space-y-1">
              <Progress value={overallProgress} className="h-2" />
              <p className="text-xs text-muted-foreground text-right font-semibold">
                {overallProgress}%
              </p>
            </div>
            <p className="text-xs text-muted-foreground text-center">
              {t('offers.import.processingHint')}
            </p>
          </div>
        )}

        {/* Success Result */}
        {result && result.success && (
          <div className="space-y-4">
            <div className="rounded-xl border border-green-200 bg-green-50 dark:bg-green-900/20 p-4">
              <div className="flex items-center gap-2 text-green-700 dark:text-green-200 text-sm">
                <CheckCircle className="h-4 w-4" />
                <div>
                  <p className="font-semibold">{t('offers.import.success')}</p>
                  <p className="text-xs text-muted-foreground">
                    {t('offers.import.successDescription')}
                  </p>
                </div>
              </div>
            </div>

            {result.offer && (
              <div className="rounded-2xl border bg-card p-4 space-y-3">
                <div className="flex items-center gap-2 text-sm font-semibold">
                  <Zap className="h-4 w-4 text-primary" />
                  <span>{t('offers.import.generatedSummary')}</span>
                </div>
                <dl className="grid grid-cols-2 gap-3 text-xs">
                  <div>
                    <dt className="text-muted-foreground">{t('createOffer.form.jobTitle')}</dt>
                    <dd className="font-medium text-foreground">{result.offer.job_title || '—'}</dd>
                  </div>
                  <div>
                    <dt className="text-muted-foreground">{t('createOffer.form.department')}</dt>
                    <dd className="font-medium text-foreground">{result.offer.department || '—'}</dd>
                  </div>
                  <div>
                    <dt className="text-muted-foreground">{t('createOffer.form.contractType')}</dt>
                    <dd className="font-medium text-foreground">{result.offer.contract_type || '—'}</dd>
                  </div>
                  <div>
                    <dt className="text-muted-foreground">{t('createOffer.form.experienceLevel')}</dt>
                    <dd className="font-medium text-foreground">{result.offer.experience_level || '—'}</dd>
                  </div>
                </dl>
                {result.extractedText && !showExtractedText && (
                  <Button
                    variant="outline"
                    size="sm"
                    className="text-xs w-full"
                    onClick={() => setShowExtractedText(true)}
                  >
                    <Eye className="h-3.5 w-3.5 mr-2" />
                    {t('offers.import.viewExtractedText')}
                  </Button>
                )}
              </div>
            )}

            {result.extractedText && showExtractedText && (
              <div className="rounded-xl border bg-muted/30 p-3 space-y-2">
                <div className="flex items-center justify-between text-xs font-semibold text-muted-foreground">
                  <span>{t('offers.import.extractedText')}</span>
                  <button
                    onClick={() => setShowExtractedText(false)}
                    className="text-muted-foreground hover:text-foreground"
                  >
                    <EyeOff className="h-3.5 w-3.5" />
                  </button>
                </div>
                <div className="max-h-40 overflow-y-auto text-xs whitespace-pre-line text-muted-foreground">
                  {result.extractedText}
                </div>
              </div>
            )}

            {result.warnings && result.warnings.length > 0 && (
              <div className="rounded-xl border border-amber-200 bg-amber-50 dark:bg-amber-900/20 p-3">
                <div className="flex items-center gap-2 text-xs font-semibold text-amber-700 dark:text-amber-200">
                  <AlertCircle className="h-3.5 w-3.5" />
                  <span>{t('offers.import.warningsTitle')}</span>
                </div>
                <ul className="mt-2 space-y-1 text-xs text-muted-foreground">
                  {result.warnings.map((warning, index) => {
                    const translationKey = WARNING_TRANSLATION_KEYS[warning]
                    const text = translationKey ? t(translationKey) : warning
                    return (
                      <li key={index}>• {text}</li>
                    )
                  })}
                </ul>
              </div>
            )}
          </div>
        )}

        {/* Actions */}
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
          {result && !result.success ? (
            <Button variant="outline" onClick={handleRetry} disabled={isProcessing} className="w-full sm:w-auto">
              <RefreshCw className="h-4 w-4 mr-2" />
              {t('offers.import.retry')}
            </Button>
          ) : (
            <div />
          )}

          <div className="flex w-full sm:w-auto gap-2">
            <Button variant="outline" onClick={handleClose} disabled={isProcessing} className="flex-1 sm:flex-none">
              {result && result.success ? t('offers.import.close') : t('common.cancel')}
            </Button>
            
            {!result && (
              <Button 
                onClick={handleImport} 
                disabled={
                  (mode === 'document' && (!selectedFile || isProcessing || aiAvailable === false)) ||
                  (mode === 'url' && (isProcessing || !urlInput))
                }
                className="flex-1 sm:flex-none min-w-[140px]"
              >
                {isProcessing ? (
                  <>
                    <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                    {t('offers.import.processingShort')}
                  </>
                ) : (
                  <>
                    <Sparkles className="h-4 w-4 mr-2" />
                    {t('offers.import.cta')}
                  </>
                )}
              </Button>
            )}
          </div>
        </div>
      </div>
    </Modal>
  )
}
